import { useToasts } from '@remotely/ui-kit';
import { useEffect, useState } from 'react';
import { useResource } from '~/api';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const NO_RESULTS: never[] = [];

export function useSearch<T>(
    query: string,
    enabled: boolean,
    search: (query: string, signal?: AbortSignal) => Promise<T[] | undefined>,
) {
    const toasts = useToasts();
    const trimmed = query.trim();
    const target = enabled && [...trimmed].length >= MIN_QUERY_LENGTH ? trimmed : '';
    const [debounced, setDebounced] = useState('');

    useEffect(() => {
        const timeout = setTimeout(() => setDebounced(target), target ? DEBOUNCE_MS : 0);
        return () => clearTimeout(timeout);
    }, [target]);

    const { data: result } = useResource(
        ({ signal }) =>
            debounced
                ? search(debounced, signal).then(
                      (results) => ({
                          query: debounced,
                          results: results!,
                          failed: false,
                      }),
                      (err: unknown) => {
                          if (signal?.aborted) throw err;
                          return { query: debounced, results: NO_RESULTS, failed: true };
                      },
                  )
                : Promise.resolve(null),
        [debounced],
    );

    const settled = result?.query === target ? result : null;
    const failed = Boolean(settled?.failed);

    useEffect(() => {
        if (failed) toasts.error('Something went wrong, please try again');
    }, [failed, toasts]);

    return {
        results: settled?.results ?? NO_RESULTS,
        pending: Boolean(target) && !settled,
        failed,
    };
}
